import React, { useEffect, useId, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import { Icon } from '@els/els-react--icon';

import { DIRECTION_LEFT, DIRECTION_RIGHT, DIRECTION_TOP, DIRECTION_BOTTOM } from './constants';

import './Resizable.scss';

/* eslint-disable no-unused-expressions */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-use-before-define */
// A focusable ARIA separator is the prescribed Window Splitter widget; eslint treats separators as non-interactive.
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */

function isHorizontal(direction) {
  return direction === DIRECTION_LEFT || direction === DIRECTION_RIGHT;
}

function getDragDistance(direction, startPoint, currentPoint) {
  if (direction === DIRECTION_LEFT || direction === DIRECTION_TOP) {
    return startPoint - currentPoint;
  }

  return currentPoint - startPoint;
}

function getSizeStyleProperty(direction, useFlexBasis) {
  if (useFlexBasis) return 'flex-basis';
  return isHorizontal(direction) ? 'width' : 'height';
}

const getElRect = (el) => {
  // Or window.getComputedStyle(el).width, window.getComputedStyle(el).height
  const rect = el.getBoundingClientRect();
  return {
    width: rect.width,
    height: rect.height,
  };
};

const getEventPoint = (e) => {
  const event = e.touches ? e.touches[0] : e;
  return (
    event && {
      x: event.clientX,
      y: event.clientY,
    }
  );
};

const KEYBOARD_RESIZE_STEP = 10;
const HORIZONTAL_RESIZE_TOOLTIP = 'Drag or press \u2190/\u2192 to resize';
const VERTICAL_RESIZE_TOOLTIP = 'Drag or press \u2191/\u2193 to resize';

function getResizeTooltip(direction) {
  return isHorizontal(direction) ? HORIZONTAL_RESIZE_TOOLTIP : VERTICAL_RESIZE_TOOLTIP;
}

function getDimension(direction) {
  return isHorizontal(direction) ? 'width' : 'height';
}

function getConfiguredRange(direction, constraints) {
  const dimension = getDimension(direction);
  return {
    min: constraints[`${dimension}Min`],
    max: constraints[`${dimension}Max`],
  };
}

function resolveCssLength(value, referenceSize) {
  if (!value || value === 'none') return undefined;

  const numericValue = Number.parseFloat(value);
  if (!Number.isFinite(numericValue)) return undefined;
  if (value.endsWith('%')) return (numericValue / 100) * referenceSize;
  if (value.endsWith('rem')) {
    const rootFontSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
    return numericValue * rootFontSize;
  }

  return numericValue;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function containsRelatedTarget(element, relatedTarget) {
  return relatedTarget instanceof Node && element?.contains(relatedTarget);
}

function Resizable({
  className,
  direction = DIRECTION_TOP,
  constraints = {},
  disabled = false,
  initializeSize = true,
  useFlexBasis = false,
  resizeHandleLabel = 'Resize panel',
  onResize,
  onDragEnd,
  style,
  children,
}) {
  const resizeTooltipId = useId();
  const containerRef = useRef(null);
  const startPointRect = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const dragRect = useRef({ width: 0, height: 0 });
  const resizeHandleRef = useRef(null);
  const resizeTooltipRef = useRef(null);
  containerRef.currentConfiguration = { constraints, direction, disabled, onResize, onDragEnd, useFlexBasis };

  const setResizeTooltipVisibility = (isVisible) => {
    if (resizeTooltipRef.current) {
      resizeTooltipRef.current.dataset.visible = `${isVisible}`;
    }
  };

  const onResizeHandleMouseEnter = (e) => {
    const handlerRect = e.currentTarget.getBoundingClientRect();
    const cursorPosition = isHorizontal(direction) ? e.clientY - handlerRect.top : e.clientX - handlerRect.left;
    const barLength = isHorizontal(direction) ? handlerRect.height : handlerRect.width;

    containerRef.current.style.setProperty('--rx-resize-tooltip-position', `${clamp(cursorPosition, 0, barLength)}px`);
    setResizeTooltipVisibility(true);
  };

  const onResizeHandleMouseLeave = (e) => {
    if (containsRelatedTarget(resizeTooltipRef.current, e.relatedTarget)) return;
    if (!resizeHandleRef.current?.matches(':focus-visible')) setResizeTooltipVisibility(false);
  };

  const onResizeHandleFocus = () => {
    if (!resizeHandleRef.current?.matches(':hover')) {
      containerRef.current?.style.removeProperty('--rx-resize-tooltip-position');
    }
    setResizeTooltipVisibility(true);
  };

  const onResizeHandleBlur = () => {
    if (!resizeHandleRef.current?.matches(':hover') && !resizeTooltipRef.current?.matches(':hover')) {
      setResizeTooltipVisibility(false);
    }
  };

  const onResizeTooltipMouseEnter = () => setResizeTooltipVisibility(true);

  const onResizeTooltipMouseLeave = (e) => {
    if (containsRelatedTarget(resizeHandleRef.current, e.relatedTarget)) return;
    if (!resizeHandleRef.current?.matches(':focus-visible')) setResizeTooltipVisibility(false);
  };

  useEffect(
    () => () => {
      // Remove listeners on unMount just in case
      document.removeEventListener('mousemove', onDragging, false);
      document.removeEventListener('touchmove', onDragging, false);
      document.removeEventListener('mouseup', onDragEndCallback, false);
      document.removeEventListener('touchend', onDragEndCallback, false);
    },
    []
  );

  useEffect(() => {
    if (!containerRef.current) return;

    if (disabled) {
      containerRef.current.style[getSizeStyleProperty(direction, useFlexBasis)] = '';
      Object.assign(dragRect.current, { width: 0, height: 0 });
      return;
    }

    Object.assign(startPointRect.current, getElRect(containerRef.current));
    Object.assign(dragRect.current, { width: 0, height: 0 });
    updateResizeHandleValue();
    if (!isEmpty(constraints) && initializeSize) {
      updateSize();
    }
  }, [
    constraints.widthMin,
    constraints.widthMax,
    constraints.heightMin,
    constraints.heightMax,
    direction,
    disabled,
    initializeSize,
    useFlexBasis,
  ]);

  const getEffectiveRange = () => {
    const { constraints: currentConstraints, direction: currentDirection } = containerRef.currentConfiguration;
    const dimension = getDimension(currentDirection);
    const { min: configuredMin, max: configuredMax } = getConfiguredRange(currentDirection, currentConstraints);
    const computedStyle = window.getComputedStyle(containerRef.current);
    const parentRect = containerRef.current.parentElement?.getBoundingClientRect();
    const referenceSize = parentRect?.[dimension] || getElRect(containerRef.current)[dimension];
    const cssMin = resolveCssLength(computedStyle[`min${dimension === 'width' ? 'Width' : 'Height'}`], referenceSize);
    const cssMax = resolveCssLength(computedStyle[`max${dimension === 'width' ? 'Width' : 'Height'}`], referenceSize);
    const min = configuredMin ?? cssMin ?? 0;
    const max = configuredMax ?? cssMax ?? Math.max(referenceSize, min + 100);

    return { min, max: Math.max(max, min) };
  };

  const getCurrentSize = () => {
    const { direction: currentDirection, useFlexBasis: currentUseFlexBasis } = containerRef.currentConfiguration;
    const dimension = getDimension(currentDirection);
    const inlineSize = Number.parseFloat(containerRef.current.style[getSizeStyleProperty(currentDirection, currentUseFlexBasis)]);

    return Number.isFinite(inlineSize) ? inlineSize : getElRect(containerRef.current)[dimension];
  };

  const updateResizeHandleValue = (requestedSize = getCurrentSize()) => {
    if (!resizeHandleRef.current || !containerRef.current) return;

    const { min, max } = getEffectiveRange();
    const size = Math.round(clamp(requestedSize, min, max));
    resizeHandleRef.current.setAttribute('aria-valuemin', `${Math.round(min)}`);
    resizeHandleRef.current.setAttribute('aria-valuemax', `${Math.round(max)}`);
    resizeHandleRef.current.setAttribute('aria-valuenow', `${size}`);
    resizeHandleRef.current.setAttribute('aria-valuetext', `${size} pixels`);
  };

  const updateResizeHandleCurrentValue = (requestedSize, range) => {
    if (!resizeHandleRef.current) return;

    const min = range?.min ?? Number(resizeHandleRef.current.getAttribute('aria-valuemin'));
    const max = range?.max ?? Number(resizeHandleRef.current.getAttribute('aria-valuemax'));
    const size = Math.round(clamp(requestedSize, min, max));
    if (range) {
      resizeHandleRef.current.setAttribute('aria-valuemin', `${Math.round(min)}`);
      resizeHandleRef.current.setAttribute('aria-valuemax', `${Math.round(max)}`);
    }
    resizeHandleRef.current.setAttribute('aria-valuenow', `${size}`);
    resizeHandleRef.current.setAttribute('aria-valuetext', `${size} pixels`);
  };

  // Manipulate directly with DOM, for performance reasons
  const applyStyleSize = (styleProperty, size, range) => {
    const { onResize: currentOnResize, useFlexBasis: currentUseFlexBasis } = containerRef.currentConfiguration;

    if (currentUseFlexBasis) {
      containerRef.current.style['flex-basis'] = `${size}px`;
    } else {
      containerRef.current.style[styleProperty] = `${size}px`;
    }

    updateResizeHandleCurrentValue(size, range);
    isFunction(currentOnResize) && currentOnResize(size);
  };

  const updateSize = () => {
    const { constraints: currentConstraints, direction: currentDirection } = containerRef.currentConfiguration;
    const { width: startWidth, height: startHeight } = startPointRect.current;
    const { width: dragWidth, height: dragHeight } = dragRect.current;

    if (isHorizontal(currentDirection)) {
      const { widthMin, widthMax } = currentConstraints;
      let width = startWidth + dragWidth;

      if (widthMin !== undefined && width < widthMin) {
        width = widthMin;
      } else if (widthMax !== undefined && width > widthMax) {
        width = widthMax;
      }

      applyStyleSize('width', width);
    } else {
      const { heightMin, heightMax } = currentConstraints;
      let height = startHeight + dragHeight;

      if (heightMin !== undefined && height < heightMin) {
        height = heightMin;
      } else if (heightMax !== undefined && height > heightMax) {
        height = heightMax;
      }

      applyStyleSize('height', height);
    }
  };

  const onDragStart = useCallback((e) => {
    if (containerRef.currentConfiguration.disabled) return;

    Object.assign(startPointRect.current, {
      ...getEventPoint(e),
      ...getElRect(containerRef.current),
    });

    document.addEventListener('mousemove', onDragging, false);
    document.addEventListener('touchmove', onDragging, false);
    document.addEventListener('mouseup', onDragEndCallback, false);
    document.addEventListener('touchend', onDragEndCallback, false);
  }, []);

  const onDragging = useCallback((e) => {
    if (containerRef.currentConfiguration.disabled) return;

    e && e.preventDefault && e.preventDefault();

    const { x, y } = getEventPoint(e);
    const { x: startX, y: startY } = startPointRect.current;

    const { direction: currentDirection } = containerRef.currentConfiguration;
    Object.assign(dragRect.current, {
      width: getDragDistance(currentDirection, startX, x),
      height: getDragDistance(currentDirection, startY, y),
    });

    updateSize();
  }, []);

  const onDragEndCallback = useCallback(() => {
    const { onDragEnd: currentOnDragEnd } = containerRef.currentConfiguration;
    document.removeEventListener('mousemove', onDragging, false);
    document.removeEventListener('touchmove', onDragging, false);
    document.removeEventListener('mouseup', onDragEndCallback, false);
    document.removeEventListener('touchend', onDragEndCallback, false);
    isFunction(currentOnDragEnd) && currentOnDragEnd();
  }, []);

  // Keyboard behavior follows the WAI-ARIA Window Splitter pattern. Arrow keys move the
  // visible edge in their named direction; Home and End move it to the effective CSS/prop limits.
  const onResizeKeyDown = useCallback((e) => {
    const {
      direction: currentDirection,
      disabled: currentDisabled,
      onDragEnd: currentOnDragEnd,
      useFlexBasis: currentUseFlexBasis,
    } = containerRef.currentConfiguration;
    if (currentDisabled) return;

    if (e.key === 'Escape' && resizeTooltipRef.current?.dataset.visible === 'true') {
      e.preventDefault();
      setResizeTooltipVisibility(false);
      return;
    }

    const increaseKey = {
      [DIRECTION_LEFT]: 'ArrowLeft',
      [DIRECTION_RIGHT]: 'ArrowRight',
      [DIRECTION_TOP]: 'ArrowUp',
      [DIRECTION_BOTTOM]: 'ArrowDown',
    }[currentDirection];
    const decreaseKey = {
      [DIRECTION_LEFT]: 'ArrowRight',
      [DIRECTION_RIGHT]: 'ArrowLeft',
      [DIRECTION_TOP]: 'ArrowDown',
      [DIRECTION_BOTTOM]: 'ArrowUp',
    }[currentDirection];

    if (![increaseKey, decreaseKey, 'Home', 'End'].includes(e.key)) return;

    e.preventDefault();
    const currentSize = getCurrentSize();
    const { min, max } = getEffectiveRange();
    let nextSize = currentSize;

    if (e.key === 'Home') nextSize = min;
    else if (e.key === 'End') nextSize = max;
    else if (e.key === increaseKey) nextSize += KEYBOARD_RESIZE_STEP;
    else if (e.key === decreaseKey) nextSize -= KEYBOARD_RESIZE_STEP;

    nextSize = clamp(nextSize, min, max);
    if (nextSize === currentSize) return;

    applyStyleSize(getSizeStyleProperty(currentDirection, currentUseFlexBasis), nextSize, { min, max });
    Object.assign(dragRect.current, { width: 0, height: 0 });
    isFunction(currentOnDragEnd) && currentOnDragEnd();
  }, []);

  return (
    <div className={cn('rx-resizable', className)} ref={containerRef} style={style}>
      {!disabled && (
        <>
          <div
            role="separator"
            ref={resizeHandleRef}
            data-e2e="resizable-handler"
            tabIndex={0}
            aria-label={resizeHandleLabel}
            aria-describedby={resizeTooltipId}
            aria-orientation={isHorizontal(direction) ? 'vertical' : 'horizontal'}
            className={`rx-resizable__handler rx-resizable__handler--${direction} rx-focusable`}
            onMouseDown={onDragStart}
            onTouchStart={onDragStart}
            onMouseEnter={onResizeHandleMouseEnter}
            onMouseLeave={onResizeHandleMouseLeave}
            onFocus={onResizeHandleFocus}
            onBlur={onResizeHandleBlur}
            onKeyDown={onResizeKeyDown}>
            {direction === DIRECTION_RIGHT && (
              <Icon className="rx-resizable__handler-icon" sprite="Drag" size="s" isDecorative textAlignment="middle" />
            )}
          </div>
          <span
            ref={resizeTooltipRef}
            id={resizeTooltipId}
            role="tooltip"
            className={`rx-resizable__handler-tooltip rx-resizable__handler-tooltip--${direction}`}
            data-visible="false"
            onMouseEnter={onResizeTooltipMouseEnter}
            onMouseLeave={onResizeTooltipMouseLeave}>
            {getResizeTooltip(direction)}
          </span>
        </>
      )}
      {children}
    </div>
  );
}

Resizable.propTypes = {
  className: PropTypes.string,
  direction: PropTypes.oneOf([DIRECTION_LEFT, DIRECTION_RIGHT, DIRECTION_TOP, DIRECTION_BOTTOM]),
  constraints: PropTypes.shape({
    widthMin: PropTypes.number,
    widthMax: PropTypes.number,
    heightMin: PropTypes.number,
    heightMax: PropTypes.number,
  }),
  disabled: PropTypes.bool,
  initializeSize: PropTypes.bool,
  useFlexBasis: PropTypes.bool,
  resizeHandleLabel: PropTypes.string,
  onResize: PropTypes.func,
  onDragEnd: PropTypes.func,
  style: PropTypes.object,
};

export default Resizable;
